import type { BuildMaturity, TreeNode } from '@capsio/contracts';
import { useState, type ReactNode } from 'react';
import {
  atcChildrenOf,
  type AtcFilter,
  canHoldAssetsOf,
  pathTo,
  ROOT_ID,
  subtreeAssetCountOf,
  typeOf,
  unitChildrenOf,
  type TreeIndex,
} from './node-tree-path.js';

/**
 * 添加：一个加号。
 *
 * 跟跳转箭头同一套笔画（同尺寸、同线宽、同圆头），因为它们在同一行里并排——
 * 两个图标粗细不一样，那一行看起来就是歪的。
 */
const ADD_ICON = (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <path d="M6 2.2 V9.8" />
      <path d="M2.2 6 H9.8" />
    </g>
  </svg>
);

/**
 * 左边栏：组织架构。
 *
 * **只有组织架构，没有资产。** 侧栏回答「我在组织架构的哪儿」，渲染区回答「这儿有什么」——
 * 一列同时干两件事就是之前不够直观的原因：一直展开加上资产卡片，
 * 二十个部门每个挂五份标准，一百多张卡片会把结构整个淹掉。
 *
 * **层级只由缩进和那条竖引导线表达**，「是什么」由行首的图标说。
 *
 * **排版梯度（C-210 对齐原型 v0.22）**：字重分档——部门 600、岗位 400、ATC 400；
 * ATC 一行收到 12.5px。图标与名称之间 margin 撑开（不是文本里塞空格字符）。
 * 替代了 C-158 时代「一列只有一种字号」的裁定——客户到场要看排版对齐原型。
 *
 * **层级类型标签已撤（C-190 补-2）**：图标 + 缩进已经表达了两次层级，
 * 文字标签是冗余的第三重，且它占的正是 ATC 编码需要的空间。
 * 保留的两个例外是**属性标记**不是层级标签：`系统`（系统部门）与 `共享`（共享岗）——
 * 撤掉层级标签后它们反而更显眼，那正是目的。
 * 树行的结构身份仍在 `data-node-type` 上（复核脚本锚它），只是不再渲染成文字。
 *
 * 于是这儿只剩一种东西、一种样式。滑过才露出来的动作剩一个「+」：
 * 在这一行下面建一份 ATC。能挂资产的行都有，跟这一行现在有没有无关——
 * 第一份就是从这儿建的。行尾「已立 / 卡位」的数见下方 `slotLabel` 的注。
 *
 * ## 折叠（C-181-补1 补-A，原型 851–892 行）
 *
 * **默认全折叠（企业根除外）。** 旧裁定「一直展开，不折叠：一套部署只服务
 * 一个企业，架构本来就那么大」至此推翻——**「一套部署只服务一个企业」不等于
 * 「树很小」**：hub 上 12 部门 / 22 岗位 / 59 张卡，全展开 93 行。企业只有
 * 一个，树照样看不过来。旧裁定的前提（规模小）与结论（不折叠）之间本来就缺一环。
 *
 * **点三角 ≠ 点行**：点三角只切换展开/折叠——不动选中、不动右侧视图
 * （整理视野）；点行只展不收、选中并切右侧视图（进去看）。两个意图混成一个，
 * 想收起的人会被带走。ATC 是叶子，行首留一个隐藏的三角占位，缩进才对齐。
 *
 * **搜索回车**：自动展开全部节点，隐藏无命中的岗位（原型 862 行）。
 */
export function NodeTree({
  index,
  rootName,
  loading,
  canCreate,
  highlightId,
  onSelect,
  onCreate,
  filter,
  revealId,
  initialOpen = 'root',
  maturity = null,
}: {
  index: TreeIndex;
  rootName: string;
  /** 还在拉数据。空树和「拉之前」长得一样，不分开就会闪一下「还没有任何节点」。 */
  loading: boolean;
  /** 有没有建 ATC 的权限。没有就整棵树都不露「+」。 */
  canCreate: boolean;
  /**
   * 要高亮哪一行。
   *
   * 选中的是资产时，外层传的是它的**归属部门**——否则整棵树没有一行是亮的，
   * 人就不知道自己站在哪儿了。
   */
  highlightId: string;
  onSelect: (id: string) => void;
  /** 在这一行下面建一份 ATC。 */
  onCreate: (id: string) => void;
  /**
   * 树筛选器：全部 / 运营 ATC / 建设 ATC，接的是 `atc.cls`。
   *
   * **卡数也跟着筛**——否则「只看建设 ATC」时行尾却显示全部卡数，两处对不上。
   * 部门整枝裁剪在 `unitChildrenOf`（补-B），树上的数与画面严格同一份。
   */
  filter: AtcFilter;
  /**
   * 搜索或深链选中了一个节点时传进来：把它的祖先链展开，让那一行露出来。
   * 只在值**变化**时展开，用户随后手动折叠不受打扰。
   */
  revealId?: string;
  /**
   * 初始展开到哪儿。默认 `'root'`（只开企业根，补-A）；判定喂 `'all'`
   * 拿整棵树——无 jsdom，点不了三角，全展开的那批断言才有对象。
   */
  initialOpen?: 'root' | 'all';
  /**
   * 建设成熟度（C-190 批三 A7）：行尾「N 卡 · 百分比」的那个比率。
   * 读派生视图（不落存储列）；null = 接口还没回来，行尾退回只报卡数。
   */
  maturity?: BuildMaturity | null;
}): ReactNode {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() =>
    initialOpen === 'all' ? new Set([ROOT_ID, ...index.byId.keys()]) : new Set([ROOT_ID]),
  );

  /*
   * 选中了一个折叠着的节点（搜索、深链）：展开它的祖先链让它露面。
   * 渲染期按 props 派生调整（React  sanctioned 的 adjust-state-when-props-change
   * 写法）：只在 revealId **变化**时展开一次，之后用户手动折叠不受打扰。
   * 不放进 useEffect——effect 里同步 setState 会触发级联重渲染（lint 拦）。
   */
  const [lastReveal, setLastReveal] = useState<string | undefined>(undefined);
  if (revealId !== lastReveal) {
    setLastReveal(revealId);
    if (revealId !== undefined) {
      const chain = pathTo(index, revealId);
      setOpenIds((prev) =>
        chain.every((id) => prev.has(id)) ? prev : new Set([...prev, ...chain]),
      );
    }
  }

  /** 点三角：只切展开/折叠，不动选中、不动右侧视图。 */
  const toggle = (id: string): void =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** 点行：只展不收（你点它是要进去看，收起来没有意义）。 */
  const openUp = (id: string): void =>
    setOpenIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));

  /*
   * 树内搜索（原型 srch）：回车生效。生效时自动展开全部节点，
   * 且隐藏无命中的岗位（原型 862 行 `if(query&&list.length===0)return`）。
   * 清空再回车恢复原状。匹配不区分大小写（树上的编码人会小写着手输）。
   */
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState('');
  const searching = query !== '';
  const effectiveOpen = searching ? new Set([ROOT_ID, ...index.byId.keys()]) : openIds;
  const hitQuery = (node: TreeNode): boolean => {
    const q = query.toLowerCase();
    return node.name.toLowerCase().includes(q) || (node.code ?? '').toLowerCase().includes(q);
  };

  /**
   * 节点图标。**取自原型 v0.22**（852／859／866／871 行），不自创：
   *
   * | 层 | 图标 | 原型依据 |
   * |---|---|---|
   * | 企业 | 🏭 | 852 行 |
   * | 部门 | 🏛️ 系统 / 🛠️ 建设 / 🏢 普通 | 859 行 `d.sys?"🏛️":(d.build?"🛠️":"🏢")` |
   * | 岗位 | 🏛️ 共享 / 💼 普通 | 树上 866 行恒为 💼，而岗位详情带（1202 行）是 `p.sys?"🏛️":"💼"`——**取后者**，因为树上也需要一眼看出共享岗 |
   * | ATC | 🤖 | 871 行 |
   *
   * **一处与指令不同**：C-158 A4.2 写「🎴」，而原型里 `🎴` **零命中**、ATC 用的是 `🤖`。
   * 指令说「按原型」，故取 🤖，并把这处差异报出去。
   */
  function iconOf(node: TreeNode | null, depth: number): string {
    if (depth === 0) return '🏭';
    const attrs = node?.attributes ?? {};
    const isSystem = attrs['isSystem'] === true;
    const kind = attrs['systemKind'];
    if (node?.typeCode === 'DEPARTMENT') {
      if (isSystem) return kind === 'enterprise_construction' ? '🛠️' : '🏛️';
      return '🏢';
    }
    if (node?.typeCode === 'POSITION') return isSystem ? '🏛️' : '💼';
    return '🤖';
  }

  /**
   * 行尾那两个数（C-190 批三 A7）：「N 卡 · 建设成熟度」。
   *
   * | 层 | 显示 | 为什么 |
   * |---|---|---|
   * | 岗位 | `N 卡 · 17%` | 岗位是卡位的持有者；比率 = active / max(卡位, 已建)，视图算 |
   * | 部门、企业 | `N 卡 · 百分比 / 未调研` | 任一岗位未调研则上级「未调研」——总量不可知不编数 |
   * | ATC | 不显示 | 它自己就是一张卡 |
   *
   * **这是对 C-170「树上不显示百分比」的修订**，不是违反：那条裁定时树上
   * 没有任何可算的百分比（加进去会解释一个不存在的符号）。建设成熟度现在
   * 有派生视图可算（批一），Michael 裁定行尾改「N 卡 · 百分比」——
   * 同一个位置，从「没有数可显示」变成「有数了」。两份说法不并存，以此为准。
   *
   * 未调研显「未调研」不显 0%：0% 会被读成「建了但一张没上线」，
   * 实际是「还没开始调研」——零态与无源的区别，树上也要守。
   */
  function slotTail(node: TreeNode | null, assets: number): { text: string; hint: string } | null {
    if (node?.typeCode === 'ATC') return null;
    /* 筛选时不显百分比：卡位与成熟度都不分运营/建设，筛着的时候只报数。 */
    if (filter !== 'all') return assets > 0 ? { text: `${assets} 卡`, hint: `这一支已立 ${assets} 张卡` } : null;

    const PCT = '建设成熟度 = 已上线卡位 / 计划卡位（超额按已建计）；「未调研」= 卡位总数还没填';
    if (node?.typeCode === 'POSITION') {
      const m = maturity?.positions.find((r) => r.positionId === node.id);
      if (m === undefined) {
        // 接口还没回来：退回只报数，不猜
        return assets > 0 ? { text: `${assets} 卡`, hint: `这一支已立 ${assets} 张卡` } : null;
      }
      const tail = m.maturity === null ? '未调研' : `${Math.round(m.maturity * 100)}%`;
      return { text: `${m.builtCount} 卡 · ${tail}`, hint: PCT };
    }
    if (assets === 0) return null;
    const m =
      node === null
        ? (maturity?.enterprise ?? undefined)
        : maturity?.departments.find((r) => r.departmentId === node.id);
    if (m === undefined) return { text: `${assets} 卡`, hint: `这一支已立 ${assets} 张卡` };
    const tail = m.maturity === null ? '未调研' : `${Math.round(m.maturity * 100)}%`;
    return { text: `${m.builtCount} 卡 · ${tail}`, hint: PCT };
  }

  function renderRow(id: string, name: string, node: TreeNode | null, depth: number): ReactNode {
    /*
     * ## 四层一棵树（C-161 A3）
     *
     * 下级 = 组织单元 + **这一站自己的 ATC**。此前树只列组织单元，
     * ATC 靠行尾一个跳转按钮切到另一页——那是三页签时代的形状。
     *
     * 现在企业 → 部门 → 岗位 → ATC 在同一棵树上，点 ATC 直接进卡页。
     * 楼的比喻在树上就成立了：楼 → 层 → 房间，而不是「楼层看完再换个页面看房间」。
     */
    const units = unitChildrenOf(index, id, filter);
    const cards = atcChildrenOf(index, id, filter);
    const children = [...units, ...cards];
    // 整支往下数：部门自己没挂卡、但它的岗位下有，行尾那个数要把整支算进去
    const assets = subtreeAssetCountOf(index, id, filter);
    const disabled = node !== null && !node.enabled;

    const type = node === null ? undefined : typeOf(index, node);
    const isAsset = node !== null && type?.kind === 'ASSET';
    const open = effectiveOpen.has(id);

    /* 搜索时隐藏无命中的岗位（原型 862 行）：一张命中的卡都没有就整行不出。 */
    if (searching && node?.typeCode === 'POSITION' && !cards.some(hitQuery)) return null;

    const rowClass = ['node-row', highlightId === id ? 'sel' : '', disabled ? 'off' : '']
      .filter((part) => part !== '')
      .join(' ');

    return (
      <div key={id} className="node-branch">
        {/* data-node-type：层级身份的结构出口（复核脚本锚它）。文字标签已撤（补-2）。 */}
        <div className={rowClass} data-node-type={node === null ? 'ENTERPRISE' : node.typeCode}>
          {/*
            行首三角。**点它只切换展开/折叠**——不动选中、不动右侧视图；
            点行的其余部分才是「进去看」（openUp + onSelect）。
            ATC 是叶子，占位但不可见：缩进对齐靠它（原型 872 行 visibility:hidden）。
          */}
          {isAsset ? (
            <span className="node-car" style={{ visibility: 'hidden' }} aria-hidden="true">
              ▶
            </span>
          ) : (
            <span
              className={open ? 'node-car open' : 'node-car'}
              role="button"
              aria-label={open ? `收起「${name}」` : `展开「${name}」`}
              onClick={(event) => {
                event.stopPropagation();
                toggle(id);
              }}
            >
              ▶
            </span>
          )}
          <button
            type="button"
            className="node-btn"
            onClick={() => {
              openUp(id);
              onSelect(id);
            }}
          >
            {/*
              层级身份不再落成文字标签（补-2：图标 + 缩进已表达两次，
              文字是冗余的第三重）；结构身份在 `.node-row` 的 `data-node-type` 上。
              行首只剩图标，行尾只剩那两个数——扫这棵树时少了两列噪音。
            */}
            <span className="node-ic" aria-hidden="true">
              {iconOf(node, depth)}
            </span>

            <span className="label">
              {/*
                ATC 显编码不显名称（C-190 补-1）：**编号是房间号**——
                在一层楼里找房间看的是门牌号，名称是进门之后的事（悬停可见）。
                等宽字体，所有 ATC 节点视觉等宽，像一排门牌号。
              */}
              {isAsset && node?.code != null && node.code !== '' ? (
                <span className="mono" title={name}>
                  {node.code}
                </span>
              ) : (
                name
              )}
              {node?.attributes['isSystem'] === true && (
                /* 属性标记（不是层级标签）：部门标「系统」，岗位标「共享」。 */
                <span className="tag sys">
                  {node.typeCode === 'DEPARTMENT' ? '系统' : '共享'}
                </span>
              )}
              {disabled && <span className="off-tag">已停用</span>}
            </span>
          </button>

          {/*
            两个动作都滑过或键盘聚焦时才露出来。键盘一定要能看见它们——
            否则纯键盘用户只会摸到一个空行。

            「+」的条件是「这儿能挂资产」，不是「这儿已经有资产」：
            按份数决定就永远建不出第一份。
          */}
          {canCreate && canHoldAssetsOf(index, id) && (
            <button
              type="button"
              className="node-add"
              title={`在「${name}」下创建 ATC`}
              aria-label={`在「${name}」下创建 ATC`}
              onClick={(event) => {
                event.stopPropagation();
                onCreate(id);
              }}
            >
              {ADD_ICON}
            </button>
          )}

          {/*
            行尾显示这一支的卡数。**不再是跳转按钮**——卡已经在树上，
            「跳到另一页去看卡」这个动作随三页签一起撤了（C-161 A3）。

            岗位那一行显示的是「已立 / 卡位」，因为岗位是卡位的持有者；
            部门与企业那一行只显示已立数——卡位总数是逐岗填的，往上加总没有对应的调研结论。
          */}
          {slotTail(node, assets) !== null && (
            <span className="node-count" title={slotTail(node, assets)!.hint}>
              {slotTail(node, assets)!.text}
            </span>
          )}
        </div>
        {/*
          下级套一层容器：缩进和那条竖引导线都挂在它身上。
          原来用行内 paddingLeft 按深度算，那样画不出连续的竖线——
          线要跟着「这一支」，而不是跟着某一行。
        */}
        {open && children.length > 0 && (
          <div className="node-kids">
            {children.map((child) => renderRow(child.id, child.name, child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  /*
   * 不加标题。侧栏里只有这一样东西，标一行「组织架构」是把显而易见的事说出来，
   * 还白占一行。屏幕阅读器靠 aria-label 认它。
   */
  return (
    <div className="tree" role="tree" aria-label="组织架构">
      {/*
        树内搜索（原型 srch）。**回车才生效**：边敲边筛会让树在手下一直变。
        生效时自动展开全部、隐藏无命中的岗位；清空再回车恢复原状。
        与 ⌘K 的全局排序搜索是两回事：那个回答「全系统里找一份标准」，
        这个回答「在这张位置图里找」。
      */}
      <input
        className="tree-search"
        type="search"
        placeholder="在树里找，回车生效"
        aria-label="在树里找"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') setQuery(searchText.trim());
          if (event.key === 'Escape') {
            setSearchText('');
            setQuery('');
          }
        }}
      />
      {renderRow(ROOT_ID, rootName, null, 0)}
      {!loading && unitChildrenOf(index, ROOT_ID).length === 0 && (
        <p className="tree-empty">
          还没有组织架构。到「ATC 设置」页的资产台账里新增部门、岗位与卡。
        </p>
      )}
    </div>
  );
}
